import { useEffect, useState } from 'react'
import { Loader2, Plus, Trash2, ShieldCheck, Search, X, Pencil } from 'lucide-react'
import api from '../../services/api'
import Organisations from './Organisations.jsx'
import { useTranslation } from 'react-i18next'

/**
 * Super Admin — the platform's control panel.
 *
 *   • the product list (registering a product is the whole of "how a future product joins")
 *   • who has a role in which product
 *   • who is a Super Admin
 *
 * Nothing here touches another product's data — only the record that a person has a role there.
 * Removing a product unplugs it; that product keeps its own database entirely.
 */
export default function PlatformAdmin() {
  const { t } = useTranslation('platform')
  const [tab, setTab] = useState('overview')
  const [products, setProducts] = useState(null)
  const [access, setAccess] = useState([])
  const [supers, setSupers] = useState([])
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setError(null)
    try {
      const [p, a, s] = await Promise.all([
        api.get('/platform/products'),
        api.get('/platform/access'),
        api.get('/platform/super-admins'),
      ])
      setProducts(p.data || [])
      setAccess(a.data || [])
      setSupers(s.data || [])
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the platform settings.')
      setProducts([])
    }
  }
  useEffect(() => { load() }, [])

  if (products === null) {
    return <div className="p-6 flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900">{t('platform')}</h1>
      <p className="text-sm text-gray-500 mt-1">
        One shared login; every product keeps its own roles and data. Granting someone a role in a
        product gives them nothing in ProTrack, and nothing here is automatic.
      </p>

      {error && <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

      <div className="flex gap-1 mt-5 border-b border-gray-200">
        {[['overview', 'Overview'], ['orgs', 'Organisations'], ['products', 'Products'], ['access', 'Who has access'], ['supers', 'Super Admins']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition ${tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === 'overview' && <Overview goto={setTab} />}
        {tab === 'orgs' && <Organisations />}

        {tab === 'products' && <Products products={products} reload={load} busy={busy} setBusy={setBusy} setError={setError} />}
        {tab === 'access' && <Access products={products} access={access} reload={load} setError={setError} />}
        {tab === 'supers' && <Supers supers={supers} reload={load} setError={setError} />}
      </div>
    </div>
  )
}


/* ── Overview ─────────────────────────────────────────────────────────────── */

/**
 * The Super Admin's oversight view: the whole platform on one screen.
 *
 * Answers "what needs me?" before "what exists?". A list of products tells you nothing is wrong;
 * the attention section is what catches work falling between two systems — a document approved
 * and then never carried across, a product nobody can open, access left behind on something
 * switched off.
 */
function Overview({ goto }) {
  // `t` is already taken in this component — it holds d.totals — so the translation
  // hook is named tPlat here. Renaming their data instead would touch the tiles array
  // and every reference in it.
  const { t: tPlat } = useTranslation('platform')
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    api.get('/platform/overview')
      .then(r => { if (alive) setD(r.data) })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || 'Could not load the overview.') })
    return () => { alive = false }
  }, [])

  if (err) return <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</p>
  if (!d) return <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>

  const t = d.totals || {}
  const tiles = [
    ['Products', t.products, `${t.active_products || 0} active`],
    ['People with access', t.people_with_access, 'across all products'],
    ['Documents handed over', t.documents_handed_over, 'delivered to a product'],
    ['Super Admins', t.super_admins, 'keep this small'],
  ]

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map(([label, value, sub]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-2xl font-bold text-gray-900 tabular-nums">{value ?? '—'}</p>
            <p className="text-xs font-medium text-gray-700 mt-0.5">{label}</p>
            <p className="text-[11px] text-gray-400">{sub}</p>
          </div>
        ))}
      </div>

      {/* What needs a person — first, because it is the only part that is urgent. */}
      {d.needs_attention?.length > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800 mb-2.5">
            Needs attention · {d.needs_attention.length}
          </p>
          <ul className="space-y-2">
            {d.needs_attention.map((a, i) => (
              <li key={i} className="text-xs bg-white border border-amber-100 rounded-lg px-3 py-2">
                <span className="text-gray-900">{a.what}</span>
                <span className="block text-gray-500 mt-0.5">{a.fix}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Nothing needs attention — every product has an address, and no approved document is
          waiting to be carried across.
        </p>
      )}

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2.5">{tPlat('products')}</p>
        {d.products?.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {d.products.map(p => (
              <div key={p.id} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-start gap-2.5">
                  <span className="text-xl leading-none">{p.icon || '📦'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{p.name}</p>
                    <p className="text-[11px] text-gray-400">{p.key}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {!p.is_active && <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">off</span>}
                    {!p.has_address && <span className="text-[10px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">no address</span>}
                    <span className={`text-[10px] rounded-full px-2 py-0.5 ${p.connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {p.connected ? 'connected' : 'not connected'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-gray-50 text-center">
                  <div><p className="text-base font-bold text-gray-900 tabular-nums">{p.people}</p><p className="text-[10px] text-gray-500">people</p></div>
                  <div><p className="text-base font-bold text-gray-900 tabular-nums">{p.handed_over}</p><p className="text-[10px] text-gray-500">delivered</p></div>
                  <div>
                    <p className={`text-base font-bold tabular-nums ${p.waiting_to_be_carried ? 'text-amber-600' : 'text-gray-900'}`}>{p.waiting_to_be_carried}</p>
                    <p className="text-[10px] text-gray-500">waiting</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            No products registered yet.{' '}
            <button onClick={() => goto('products')} className="text-indigo-600 hover:underline">{tPlat('add_one')}</button>.
          </p>
        )}
      </div>

      {d.recent_access?.length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2">{tPlat('recently_granted')}</p>
          <ul className="text-xs text-gray-600 space-y-1">
            {d.recent_access.map((r, i) => (
              <li key={i} className="flex flex-wrap gap-x-1.5">
                <span className="text-gray-900">{r.person}</span>
                <span>→ {r.role} in {r.product}</span>
                {r.granted_by && <span className="text-gray-400">· by {r.granted_by}</span>}
                {r.at && <span className="text-gray-400">· {new Date(r.at).toLocaleDateString()}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}


/* ── Products ─────────────────────────────────────────────────────────────── */

function Products({ products, reload, busy, setBusy, setError }) {
  const { t } = useTranslation('platform')
  const [form, setForm] = useState({ key: '', name: '', description: '', url: '', icon: '', colour: 'blue', roles: '', submit_roles: '', receive_roles: '' })
  const [adding, setAdding] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Which product is being edited, or null when adding a new one. The backend has had
  // PUT /platform/products/{id} all along and nothing here ever called it, so the only way to
  // correct a name or an address was to delete the product and make it again — which throws away
  // its id and every access grant hanging off it, to fix a typo.
  const [editingId, setEditingId] = useState(null)

  const beginEdit = (p) => {
    setEditingId(p.id)
    setAdding(true)
    setForm({
      key: p.key || '', name: p.name || '', description: p.description || '',
      url: p.url || '', icon: p.icon || '', colour: p.colour || 'blue',
      roles: (p.roles || []).join(', '),
      submit_roles: (p.submit_roles || []).join(', '),
      receive_roles: (p.receive_roles || []).join(', '),
    })
  }

  const cancelForm = () => {
    setAdding(false); setEditingId(null)
    setForm({ key: '', name: '', description: '', url: '', icon: '', colour: 'blue', roles: '', submit_roles: '', receive_roles: '' })
  }

  const save = async () => {
    if (!form.key.trim() || !form.name.trim()) { setError('A key and a name are required.'); return }
    setBusy(true); setError(null)
    try {
      const body = { ...form, roles: form.roles.split(',').map(r => r.trim()).filter(Boolean),
        submit_roles: form.submit_roles.split(',').map(r => r.trim()).filter(Boolean),
        receive_roles: form.receive_roles.split(',').map(r => r.trim()).filter(Boolean) }
      if (editingId) await api.put(`/platform/products/${editingId}`, body)
      else await api.post('/platform/products', body)
      cancelForm()
      await reload()
    } catch (e) {
      setError(e.response?.data?.detail
        || (editingId ? 'Could not save the change.' : 'Could not add the product.'))
    }
    finally { setBusy(false) }
  }

  // Disconnecting is shown before it is done: what goes, what stays, and the export offered
  // first. Leadership asked for the clean exit to be demonstrable rather than asserted.
  const [disconnecting, setDisconnecting] = useState(null)

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">ProTrack is the host and is always available — it needs no entry here.</p>
        <button onClick={() => (adding ? cancelForm() : setAdding(true))} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5">
          {adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {adding ? 'Cancel' : 'Add a product'}
        </button>
      </div>

      {adding && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 grid gap-3 sm:grid-cols-2">
          {editingId && (
            <p className="sm:col-span-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              Editing <strong>{form.name || 'this product'}</strong>. Its access grants and everything
              people hold in it are untouched — only what is written here changes.
            </p>
          )}
          <Field label="Key (short id)" value={form.key} onChange={v => set('key', v)} placeholder="hfo" />
          <Field label="Name" value={form.name} onChange={v => set('name', v)} placeholder="HIV Financing Observatory (HFO)" />
          <Field label="Description" value={form.description} onChange={v => set('description', v)} placeholder={t('ph_assistant_desc')} className="sm:col-span-2" />
          <Field label="Address (URL, or /path inside ProTrack)" value={form.url} onChange={v => set('url', v)} placeholder="https://hfo.example.org" className="sm:col-span-2" />
          <Field label="Icon (one emoji)" value={form.icon} onChange={v => set('icon', v)} placeholder="🤖" />
          <label className="block">
            <span className="text-xs font-medium text-gray-600 block mb-1">{t('tile_colour')}</span>
            <select value={form.colour} onChange={e => set('colour', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm">
              {['indigo', 'blue', 'emerald', 'amber', 'rose', 'purple', 'slate'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Field label="Its roles (comma separated)" value={form.roles} onChange={v => set('roles', v)}
                 placeholder="Country user, Partner, HFO Admin" className="sm:col-span-2" />
          <Field label="Roles that may submit a room's file (comma separated)" value={form.submit_roles} onChange={v => set('submit_roles', v)} placeholder="Admin" className="sm:col-span-2" />
          <Field label="Roles told when a file is submitted (comma separated)" value={form.receive_roles} onChange={v => set('receive_roles', v)} placeholder="Admin" className="sm:col-span-2" />
          <p className="sm:col-span-2 text-[11px] text-gray-400">
            These role names are used only when granting access — ProTrack never interprets another
            product's roles.
          </p>
          <button onClick={save} disabled={busy}
            className="sm:col-span-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg py-2">
            {busy ? (editingId ? 'Saving…' : 'Adding…') : (editingId ? 'Save changes' : 'Add product')}
          </button>
        </div>
      )}

      {products.length === 0 ? (
        <p className="text-sm text-gray-400">{t('no_other_products')}</p>
      ) : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-3 flex items-start gap-3">
              <span className="text-xl leading-none mt-0.5">{p.icon || '📦'}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">
                  {p.name} <span className="text-gray-300 font-normal">· {p.key}</span>
                  {!p.is_active && <span className="ml-2 text-[10px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">inactive</span>}
                </p>
                {p.description && <p className="text-xs text-gray-500">{p.description}</p>}
                <p className="text-[11px] text-gray-400 mt-1 truncate">{p.url || 'no address set'}</p>
                {p.roles?.length > 0 && <p className="text-[11px] text-gray-400">Roles: {p.roles.join(' · ')}</p>}
              </div>
              {/* Edit sits beside disconnect on purpose, and first: correcting a name was only
                  reachable by deleting the product, which is the destructive one. */}
              <button onClick={() => beginEdit(p)} title="Edit this product"
                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => setDisconnecting(p)} title={t('t_disconnect')}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {disconnecting && (
        <DisconnectModal product={disconnecting} onClose={() => setDisconnecting(null)}
                         onDone={async () => { setDisconnecting(null); await reload() }}
                         setError={setError} />
      )}
    </div>
  )
}


/* ── Disconnect ───────────────────────────────────────────────────────────── */

function DisconnectModal({ product, onClose, onDone, setError }) {
  const { t } = useTranslation('platform')
  const [preview, setPreview] = useState(null)
  const [exported, setExported] = useState(false)
  const [busy, setBusy] = useState(null)
  const [local, setLocal] = useState(null)

  useEffect(() => {
    let alive = true
    api.get(`/platform/products/${product.id}/disconnect-preview`)
      .then(r => { if (alive) setPreview(r.data) })
      .catch(e => { if (alive) setLocal(e.response?.data?.detail || 'Could not check what would be removed.') })
    return () => { alive = false }
  }, [product.id])

  const exportNow = async () => {
    setBusy('export'); setLocal(null)
    try {
      const res = await api.get(`/platform/products/${product.id}/export`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url; a.download = `${product.key}-export.zip`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      // An archive whose files could not be read from storage is NOT a complete export, and
      // reporting it as one would be the worst possible moment to be optimistic.
      const missing = Number(res.headers?.['x-export-files-missing'] || 0)
      const docs = Number(res.headers?.['x-export-documents'] || 0)
      setExported({ docs, missing })
      if (missing > 0) {
        setLocal(`Downloaded, but ${missing} file(s) could not be read from storage and are placeholders `
                 + `in the archive (see MISSING-FILES.txt). Resolve that before disconnecting.`)
      }
    } catch (e) {
      setLocal('The export could not be built. Nothing has been changed — do not disconnect until this works.')
    } finally { setBusy(null) }
  }

  const disconnect = async () => {
    setBusy('remove'); setLocal(null)
    try { await api.delete(`/platform/products/${product.id}`); await onDone() }
    catch (e) { setLocal(e.response?.data?.detail || 'Could not disconnect the product.'); setBusy(null) }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[88vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">Disconnect {product.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{t('exactly_what')}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {local && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{local}</p>}
          {!preview ? (
            <p className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking…</p>
          ) : (
            <>
              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-red-700 mb-2">{t('will_be_removed')}</p>
                <ul className="space-y-2">
                  {preview.will_be_removed.map((r, i) => (
                    <li key={i} className="text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                      <span className="font-semibold text-red-900">{r.what}{r.count != null ? ` — ${r.count}` : ''}</span>
                      <span className="block text-red-800/80 mt-0.5">{r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-[11px] font-bold tracking-wide uppercase text-green-700 mb-2">{t('will_not_touch')}</p>
                <ul className="space-y-2">
                  {preview.will_not_be_touched.map((r, i) => (
                    <li key={i} className="text-xs bg-green-50 border border-green-100 rounded-lg px-3 py-2">
                      <span className="font-semibold text-green-900">{r.what}{r.count != null ? ` — ${r.count}` : ''}</span>
                      <span className="block text-green-800/80 mt-0.5">{r.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                <p className="text-xs text-amber-900 mb-2">{preview.advice}</p>
                <button onClick={exportNow} disabled={busy === 'export'}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg px-3 py-1.5">
                  {busy === 'export' ? 'Building…' : exported ? 'Download again' : 'Take the export first'}
                </button>
                {exported && (
                  <span className={`ml-2 text-[11px] font-semibold ${exported.missing ? 'text-red-700' : 'text-green-700'}`}>
                    {exported.missing
                      ? `⚠ downloaded — ${exported.missing} file(s) missing`
                      : `✓ downloaded — ${exported.docs} document(s), all files included`}
                  </span>
                )}
              </div>

              <div className="flex gap-2 pt-1">
                <button onClick={disconnect} disabled={busy === 'remove'}
                  className="flex-1 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg py-2">
                  {busy === 'remove' ? 'Disconnecting…' : `Disconnect ${product.name}`}
                </button>
                <button onClick={onClose} className="text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg px-4">
                  Cancel
                </button>
              </div>
              {!exported && (
                <p className="text-[11px] text-gray-400 text-center">
                  You can disconnect without exporting — but then the record only exists here.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}


/* ── Access ───────────────────────────────────────────────────────────────── */

function Access({ products, access, reload, setError }) {
  const { t } = useTranslation('platform')
  const [email, setEmail] = useState('')
  const [productId, setProductId] = useState('')
  const [role, setRole] = useState('')
  const [found, setFound] = useState(null)
  const [busy, setBusy] = useState(false)

  const chosen = products.find(p => String(p.id) === String(productId))

  const lookup = async () => {
    if (!email.trim()) return
    setError(null); setFound(null)
    try {
      const { data } = await api.get('/platform/lookup', { params: { email: email.trim() } })
      setFound(data)
    } catch (e) { setError(e.response?.data?.detail || 'Could not look that up.') }
  }

  const grant = async () => {
    if (!email.trim() || !productId || !role.trim()) { setError('Email, product and role are all needed.'); return }
    setBusy(true); setError(null)
    try {
      const { data } = await api.post('/platform/access', {
        email: email.trim(), product_id: Number(productId), product_role: role.trim(),
      })
      setEmail(''); setRole(''); setFound(null)
      await reload()
      window.alert(data.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not grant access.') }
    finally { setBusy(false) }
  }

  const revoke = async (a) => {
    if (!window.confirm(`Remove ${a.user_name}'s ${a.product_role} access to ${a.product_name}?`)) return
    setError(null)
    try { await api.delete(`/platform/access/${a.id}`); await reload() }
    catch (e) { setError(e.response?.data?.detail || 'Could not remove access.') }
  }

  return (
    <div>
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-3">{t('give_access')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="text-xs font-medium text-gray-600 block mb-1">{t('their_email')}</span>
            <div className="flex gap-2">
              <input value={email} onChange={e => setEmail(e.target.value)} onBlur={lookup}
                placeholder="person@example.org"
                className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" />
              <button onClick={lookup} className="px-2.5 text-gray-500 hover:text-indigo-600 border border-gray-300 rounded-lg" title={t('t_check_email')}>
                <Search className="w-4 h-4" />
              </button>
            </div>
          </label>

          {found && (
            <div className={`sm:col-span-2 text-xs rounded-lg px-3 py-2 border ${found.exists ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              {found.exists ? (
                <>
                  <span className="font-semibold">{found.full_name}</span> already has an account
                  ({found.protrack_role} in ProTrack{found.products?.length ? `; ${found.products.map(p => `${p.role} in ${p.name}`).join('; ')}` : ''}).
                  The new role is added to this same account — one email is always one login.
                </>
              ) : (
                <>No account with that email yet. Invite them first, then come back and add the product role.</>
              )}
            </div>
          )}

          <label className="block">
            <span className="text-xs font-medium text-gray-600 block mb-1">{t('product')}</span>
            <select value={productId} onChange={e => { setProductId(e.target.value); setRole('') }}
              className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm">
              <option value="">{t('choose')}</option>
              {products.filter(p => p.is_active).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600 block mb-1">{t('role_inside')}</span>
            {chosen?.roles?.length > 0 ? (
              <select value={role} onChange={e => setRole(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm">
                <option value="">{t('choose')}</option>
                {chosen.roles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            ) : (
              <input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Country user"
                className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" />
            )}
          </label>

          <button onClick={grant} disabled={busy || !found?.exists}
            className="sm:col-span-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg py-2">
            {busy ? 'Granting…' : 'Grant access'}
          </button>
        </div>
      </div>

      {access.length === 0 ? (
        <p className="text-sm text-gray-400">{t('nobody_granted')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">{t('person')}</th><th className="py-2 pr-3">{t('product')}</th>
                <th className="py-2 pr-3">{t('role_there')}</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {access.map(a => (
                <tr key={a.id} className="border-b border-gray-50">
                  <td className="py-2 pr-3">
                    <span className="text-gray-900">{a.user_name}</span>
                    <span className="block text-[11px] text-gray-400">{a.user_email}</span>
                  </td>
                  <td className="py-2 pr-3 text-gray-700">{a.product_name}</td>
                  <td className="py-2 pr-3 text-gray-700">{a.product_role}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => revoke(a)} className="text-xs text-gray-400 hover:text-red-600">{t('remove')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


/* ── Super Admins ─────────────────────────────────────────────────────────── */

function Supers({ supers, reload, setError }) {
  const { t } = useTranslation('platform')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!email.trim()) return
    setBusy(true); setError(null)
    try {
      const { data: found } = await api.get('/platform/lookup', { params: { email: email.trim() } })
      if (!found.exists) { setError('No account with that email.'); return }
      const { data } = await api.put(`/platform/super-admin/${found.user_id}`, { is_super_admin: true })
      setEmail(''); await reload(); window.alert(data.detail)
    } catch (e) { setError(e.response?.data?.detail || 'Could not grant Super Admin.') }
    finally { setBusy(false) }
  }

  const remove = async (u) => {
    if (!window.confirm(`Remove Super Admin from ${u.full_name}?`)) return
    setError(null)
    try { await api.put(`/platform/super-admin/${u.id}`, { is_super_admin: false }); await reload() }
    catch (e) { setError(e.response?.data?.detail || 'Could not remove Super Admin.') }
  }

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
        <p className="text-xs text-amber-900">
          <ShieldCheck className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
          A Super Admin sees <strong>every</strong> product's dashboard. This is granted separately
          from being a ProTrack Admin on purpose — so nobody becomes all-powerful by accident. Keep
          it rare.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder={t('ph_their_email')}
          className="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" />
        <button onClick={add} disabled={busy}
          className="text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 rounded-lg px-3">
          {busy ? '…' : 'Make Super Admin'}
        </button>
      </div>

      {supers.length === 0 ? (
        <p className="text-sm text-gray-400">{t('no_supers')}</p>
      ) : (
        <div className="space-y-2">
          {supers.map(u => (
            <div key={u.id} className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3">
              <ShieldCheck className="w-4 h-4 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">{u.full_name}</p>
                <p className="text-[11px] text-gray-400">{u.email}</p>
              </div>
              <button onClick={() => remove(u)} className="text-xs text-gray-400 hover:text-red-600">{t('remove')}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


/* ── shared ───────────────────────────────────────────────────────────────── */

function Field({ label, value, onChange, placeholder, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-gray-600 block mb-1">{label}</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
    </label>
  )
}
